import React from 'react';
import EventEmitter from 'eventemitter3';
import classnames from 'classnames';

import { ITaggedProductList } from '../../models/TaggedProductList';
import { ManningOnlineStore } from '../../store/ManningOnlineStore';
import { IProductDescription } from '../../../ts/models/product/IProductDescription';
import { TaggedProductList } from '../../../ts/components/common/taggedProductList/TaggedProductList';
import { ProductCard } from '../../../ts/components/common/productCard/ProductCard';
import { ProductDetailsPanel } from '../../../ts/components/common/productDetailsPanel/ProductDetailsPanel';

import './tagged-product-list-with-details.scss';
import { DiscussionSummary } from '../discussionSummary/DiscussionSummary';

export interface IProps {
    id?: any;
    store: ManningOnlineStore;
    taggedProductList: ITaggedProductList;

    pretitle?: string;
    subtitle?: string;
    onTitleClick?: () => void;

    defaultOnHoverLabel?: string;

    onRemoveProductClick?: (product: IProductDescription) => void;
    disableSelection?: boolean;
    
    advancedOperation?: {
        id: any;
        emailTitle: string;
        share: (id: any) => Promise<string>;
        onChangeTitle: () => void;
        onDelete: () => void;
        onReorder: () => void;
    },

    onDragEnter?: (e) => void;
    onDragOver?: (e) => void;
    onDragLeave?: (e) => void;
    onDragDrop?: (e) => void;
    onRootRef?: (ref) => void;

    isReordering?: boolean;
    
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    
    onCloseClick?: () => void;

    children?: React.ReactNode;
}

export interface IState {
    selectedItem: IProductDescription;
    relatedProduct: IProductDescription;
    
    selectedItemReadingPercentageFinished: number;
    showSelectedItemReadingPercentage: boolean;

    maxDisplayedProducts: number;
}

let currentlyOpenTaggedProductListKey: string;
const changeEmitter = new EventEmitter();

function triggerChange(key: string){
    changeEmitter.emit("tagged-product-list", key);
}

function onDetailsPanelOpened(callback: (key: string) => void){
    changeEmitter.addListener("tagged-product-list", callback);
}

function offDetailsPanelOpened(callback: (key: string) => void){
    changeEmitter.removeListener("tagged-product-list", callback);
}

export class TaggedProductListWithDetails extends React.Component <IProps, IState> {
    private hasBeenMounted: boolean;
    
    constructor(props: IProps){
        super(props);

        this.selectProduct = this.selectProduct.bind(this);
        
        this.handleAddRelatedItemToReadingListRequest = this.handleAddRelatedItemToReadingListRequest.bind(this);
        this.handleProductDetailsRequested = this.handleProductDetailsRequested.bind(this);
        this.handleAnyDetailsPanelOpen = this.handleAnyDetailsPanelOpen.bind(this);
        this.showAllProducts = this.showAllProducts.bind(this);
        
        this.state = {
            selectedItem: null,
            relatedProduct: null,
            selectedItemReadingPercentageFinished: undefined,
            showSelectedItemReadingPercentage: true,
            maxDisplayedProducts: 25
        };
    }

    public componentDidMount(){
        this.hasBeenMounted = true;

        onDetailsPanelOpened(this.handleAnyDetailsPanelOpen);
    }

    public componentWillUnmount(){
        this.hasBeenMounted = false;

        offDetailsPanelOpened(this.handleAnyDetailsPanelOpen);
    }

    public render(){
        const productList = this.props.taggedProductList;
        const allProducts = productList.products;
        const displayedProducts = allProducts.slice(0, this.state.maxDisplayedProducts);

        return <div className="tagged-product-list-with-details-root" 
                    data-tagged-product-list-id={this.props.id || -1}
                    onDragEnter = {this.props.onDragEnter}
                    onDragOver = {this.props.onDragOver}
                    onDragLeave = {this.props.onDragLeave}
                    onDrop = { this.props.onDragDrop}
                    ref={this.props.onRootRef}>
                    <TaggedProductList key={productList.title} tag={productList.title || "unnamed"} 
                                       onTitleClick={this.props.onTitleClick}
                                       pretitle={this.props.pretitle}
                                       subtitle={this.props.subtitle}
                                       showSlideArrows={true}
                                       showMoreInfoCardOnClick={false}
                                       store={this.props.store}
                                       advancedOperations={this.props.advancedOperation}
                                       onMoveUp={this.props.onMoveUp}
                                       onMoveDown={this.props.onMoveDown}
                                       isReordering={this.props.isReordering}
                                       onCloseClick={this.props.onCloseClick}>
                        { displayedProducts.map(it => 
                            <ProductCard key={it.id}
                                         className={classnames({"is-selected": this.state.selectedItem != null && it.id == this.state.selectedItem.id})}
                                         productDescription={it} 
                                         coverPlaceholder={this.props.store.coverPlaceholder}
                                         onClick={this.selectProduct}
                                         onCloseButtonClick={this.props.onRemoveProductClick}
                                         wideCoverPlaceholder={this.props.store.wideCoverPlaceholder}
                                         showPercentage={this.state.showSelectedItemReadingPercentage && this.state.selectedItem != null && it.id == this.state.selectedItem.id}
                                         percentageRead={this.state.selectedItemReadingPercentageFinished}
                                         onHoverLabel={ this.props.defaultOnHoverLabel || "resume"}
                                         data={`${it.manningId}`}
                                         onDragStart={this.props.store.catalogState.coversAreDraggable ? this.props.store.onProductCoverDragStart : null}/>)}
                        { this.props.children }
                        {
                            displayedProducts.length < allProducts.length ? 
                            <button onClick={this.showAllProducts} className="_show-all-button"> show all </button> : null
                        }
                    </TaggedProductList>
                    { this.renderDetailsSection()}
                </div>
    }

    private showAllProducts(){
        this.setState({
            maxDisplayedProducts: 50000
        });
    }

    private renderDetailsSection(){
        const selectedItem = this.state.selectedItem;
        const relatedProduct = this.state.relatedProduct;
        const store = this.props.store;

        if(selectedItem == null 
        && (this.props.taggedProductList == null
            || this.props.taggedProductList.products == null
            || this.props.taggedProductList.products.length == 0)) {
            return null;
        }
        
        return <ProductDetailsPanel coverPlaceholder={store.coverPlaceholder}
                                    wideCoverPlaceholder={store.wideCoverPlaceholder}
                                    product={selectedItem}
                                    relatedProduct={relatedProduct == null ? null : { ... this.state.relatedProduct, isInReadingList: this.props.store.isInAtLeastOneReadingList(relatedProduct)}}
                                    onProductDetailsRequested={this.handleProductDetailsRequested}
                                    onAddToReadingListRequested={this.handleAddRelatedItemToReadingListRequest}
                                    showRemoveFromReadingList={this.props.onRemoveProductClick != null}
                                    onRemoveFromReadingList={(product) => {
                                        this.props.onRemoveProductClick(product);
                                        this.selectProduct(null);
                                    }}>
                    <DiscussionSummary store={this.props.store} product={this.state.selectedItem}/>
                </ProductDetailsPanel>
    }

    private handleAnyDetailsPanelOpen(key: string){
        if(this.hasBeenMounted && this.state.selectedItem != null && this.props.taggedProductList.title != key){
            this.selectProduct(null)
        }
    }

    private selectProduct(product: IProductDescription){
        if(this.props.disableSelection) { return; }
        
        if(product != null){
            if(currentlyOpenTaggedProductListKey && currentlyOpenTaggedProductListKey != this.props.taggedProductList.title){
                triggerChange(this.props.taggedProductList.title);
            }
            
            currentlyOpenTaggedProductListKey = this.props.taggedProductList.title;
            this.props.store.getProductPercentageFinished(product.manningId).then(percentageFinished => {
                this.setState({
                    selectedItemReadingPercentageFinished: percentageFinished,
                    showSelectedItemReadingPercentage: percentageFinished > 0
                });
            }).catch(e => {
                this.setState({
                    selectedItemReadingPercentageFinished: undefined,
                    showSelectedItemReadingPercentage: false
                });
            });
        }
        
        this.setState({
            selectedItem: product,
            selectedItemReadingPercentageFinished: undefined,
            showSelectedItemReadingPercentage: true,
        });
    }

    private handleAddRelatedItemToReadingListRequest(product: IProductDescription){
        this.props.store.requestReadingListSpecificationForProduct(product);
    }

    private timoutId: any;
    private lastRequestedProductId: any;
    
    private handleProductDetailsRequested(productId: any){
        clearTimeout(this.timoutId);
        this.lastRequestedProductId = productId;

        if(!productId) {
            this.setState({
                relatedProduct: null
            });
            
            return;
        }
        
        this.timoutId = setTimeout(() => {
            this.setState({
                relatedProduct: this.props.store.dummyProduct
            });

            this.props.store.getProductDescription(productId).then(productDescription => {
                if(this.lastRequestedProductId == productDescription.manningId){
                    this.setState({
                        relatedProduct: productDescription
                    });
                }
            });
        }, 64);
    }
}
